import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Divider,
    Grid
} from '@mui/material';
import ETLogo from '../../../../assets/environment-testing.png';

interface PrintComponentProps {
    formData: any;
    asmOptions: any[];
    eleCodes: any[];
    buCodes: any[];
    newPriceList: any[];
    paymentTermsOptions: any[];
    australianStates: any[];
    countryOptions: string[];
    signatureImage: any;
}

export const PrintComponent: React.FC<PrintComponentProps> = ({
    formData,
    asmOptions,
    eleCodes,
    buCodes,
    newPriceList,
    paymentTermsOptions,
    australianStates,
    countryOptions,
    signatureImage
}) => {
    // Helper functions to get display values
    const getAsmName = (asmId: any) => {
        const asm = asmOptions?.find(opt => opt.userID === asmId);
        return asm ? asm.asmName : asmId;
    };

    const getPriceListName = (priceListId: any) => {
        const priceList = newPriceList?.find(opt => opt.id === Number(priceListId));
        return priceList ? priceList.name : priceListId;
    };

    const getPaymentTermsDescription = (value: any) => {
        const term = paymentTermsOptions?.find(opt => opt.value === value);
        return term ? term.description : value;
    };

    const getStateName = (stateId: any) => {
        if (stateId === null || stateId === undefined || stateId === '') {
            return '-';
        }

        const state = australianStates?.find(opt =>
            String(opt?.id) === String(stateId)
            || String(opt?.value) === String(stateId)
            || String(opt?.name) === String(stateId)
        );

        return state?.name || state?.value || String(stateId);
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString();
    };

    const toYesNo = (value: any) => {
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }

        if (typeof value === 'string') {
            return value.trim().toLowerCase() === 'yes' ? 'Yes' : 'No';
        }

        return value ? 'Yes' : 'No';
    };

    const printDate = new Date().toLocaleDateString('en-AU', {
        day: '2-digit', month: '2-digit', year: 'numeric'
    });

    const printStyles = `
        /* Screen + iframe print styles */
        body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 13px;
            color: #000;
            background: #fff;
            margin: 0;
            padding: 0;
        }
        .print-container {
            position: relative;
            box-sizing: border-box;
        }
        /* Page layout table — repeats thead/tfoot spacer on every printed page */
        .page-layout-table {
            width: 100%;
            table-layout: auto !important;
            border-collapse: collapse;
            border: none !important;
        }
        .page-layout-table thead {
            display: table-header-group;
        }
        .page-layout-table tfoot {
            display: table-footer-group;
        }
        .page-layout-spacer {
            height: 0.6cm;
            border: none !important;
            padding: 0 !important;
            background: transparent !important;
        }
        .page-layout-footer-spacer {
            height: 1.8cm;
            border: none !important;
            padding: 0 !important;
            background: transparent !important;
        }
        .page-layout-table > tbody > tr > td.page-layout-content {
            padding: 0 0.8cm !important;
            border: none !important;
            width: auto !important;
            max-width: none !important;
            min-width: 0 !important;
            background: transparent !important;
        }
        /* Data tables — exclude page-layout-table */
        .print-container table:not(.page-layout-table) {
            table-layout: fixed !important;
            width: 100% !important;
            border-collapse: collapse !important;
            border: 1px solid #999 !important;
        }
        .print-container table:not(.page-layout-table) thead {
            display: table-header-group;
        }
        .print-container table:not(.page-layout-table) th,
        .print-container table:not(.page-layout-table) td {
            border: 1px solid #999 !important;
            padding: 10px 14px !important;
            font-size: 13px !important;
            line-height: 1.35 !important;
            word-wrap: break-word !important;
            overflow: hidden !important;
            vertical-align: top !important;
        }
        .print-container table:not(.page-layout-table) tr {
            break-inside: avoid;
            page-break-inside: avoid;
        }
        .print-container table:not(.page-layout-table) th {
            background-color: #f0f0f0 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            font-weight: bold !important;
            text-align: left !important;
        }
        .print-container table:not(.page-layout-table) td {
            text-align: left !important;
        }
        .print-container table:not(.page-layout-table) th:first-child,
        .print-container table:not(.page-layout-table) td:first-child {
            width: 40% !important;
            max-width: 40% !important;
            min-width: 40% !important;
        }
        .print-container table:not(.page-layout-table) th:last-child,
        .print-container table:not(.page-layout-table) td:last-child {
            width: 60% !important;
            max-width: 60% !important;
            min-width: 60% !important;
        }
        .print-container .MuiPaper-root {
            box-shadow: none !important;
            border: none !important;
        }
        .print-section-title {
            font-weight: bold;
            font-size: 16px;
            color: #003883;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            border-bottom: 2px solid #003883;
            padding-bottom: 6px;
            margin-bottom: 12px;
            margin-top: 24px;
            break-after: avoid;
            page-break-after: avoid;
        }
        .print-section-block {
        }
        .print-header {
            margin-bottom: 36px;
        }
        .print-logo-header {
            line-height: 0;
            margin-bottom: 14px;
            text-align: left;
        }
        .print-logo-header img {
            height: 50px;
            width: auto;
        }
        .print-title-header {
            width: 100%;
            text-align: center;
        }
        .print-title-header .MuiTypography-root {
            font-size: 18px !important;
            line-height: 1.2 !important;
            font-weight: 700 !important;
            color: #111 !important;
        }
        .print-footer {
            display: none;
        }
        .print-footer-layer {
            display: none;
        }
        .print-generated-footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 0.8cm 12px;
            font-size: 10px;
            color: #333;
            background: #fff;
            box-sizing: border-box;
        }
        .print-generated-footer-date {
            min-width: 110px;
        }
        .print-generated-footer-company {
            flex: 1;
            text-align: center;
            padding: 0 18px;
        }
        .print-generated-footer-page {
            min-width: 90px;
            text-align: right;
        }
        @page {
            size: A4;
            margin: 0;
        }
        @media print {
            .print-footer-layer {
                display: block;
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                pointer-events: none;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                z-index: 9999;
            }
        }
    `;

    return (
        <div className="print-container" style={{ display: 'none' }}>
            <style>{printStyles}</style>
            <div className="print-footer-layer"></div>
            <table className="page-layout-table">
                <thead><tr><td className="page-layout-spacer"></td></tr></thead>
                <tfoot><tr><td className="page-layout-footer-spacer"></td></tr></tfoot>
                <tbody><tr><td className="page-layout-content">
                    <Box sx={{ p: 0 }}>
                        {/* Header */}
                        <div className="print-header">
                            <div className="print-logo-header">
                                <img src={ETLogo} alt="Eurofins" />
                            </div>
                            <Box className="print-title-header">
                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0 }}>
                                    Client Registration Details
                                </Typography>
                            </Box>
                        </div>

                        {/* General Information */}
                        <div className="print-section-block">
                            <div className="print-section-title">General Information</div>
                            <TableContainer component={Paper} sx={{ mb: 3 }}>
                                <Table size="small">
                                    <colgroup><col style={{ width: '40%' }} /><col style={{ width: '60%' }} /></colgroup>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Name / Company Name / Business Name</TableCell>
                                            <TableCell>{formData?.generalInfo?.companyName || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>ABN</TableCell>
                                            <TableCell>{formData?.generalInfo?.abn || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Trading Name</TableCell>
                                            <TableCell>{formData?.generalInfo?.tradingName || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Name of Eurofins Contact</TableCell>
                                            <TableCell>{formData?.generalInfo?.eurofinsContactName || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Contact Name(Full name)</TableCell>
                                            <TableCell>{formData?.generalInfo?.contactName || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Email Address</TableCell>
                                            <TableCell>{formData?.generalInfo?.emailAddress || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Phone Number</TableCell>
                                            <TableCell>{formData?.generalInfo?.phoneNumber || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Mobile Number</TableCell>
                                            <TableCell>{formData?.generalInfo?.mobileNumber || '-'}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>

                        {/* Business Address */}
                        <div className="print-section-block">
                            <div className="print-section-title">Business Address</div>
                            <TableContainer component={Paper} sx={{ mb: 3 }}>
                                <Table size="small">
                                    <colgroup><col style={{ width: '40%' }} /><col style={{ width: '60%' }} /></colgroup>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Number / Street Name</TableCell>
                                            <TableCell>{formData?.generalInfo?.businessStreetNumber || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Suburb</TableCell>
                                            <TableCell>{formData?.generalInfo?.businessSuburb || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Post Code</TableCell>
                                            <TableCell>{formData?.generalInfo?.businessPostCode || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Country</TableCell>
                                            <TableCell>{formData?.generalInfo?.businessCountry || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>State</TableCell>
                                            <TableCell>{formData?.generalInfo?.businessState || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Is Billing address same as business address?</TableCell>
                                            <TableCell>{formData?.generalInfo?.isPostalSameAsBusiness ? 'Yes' : 'No'}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>

                        {/* Billing Address */}
                        <div className="print-section-block">
                            <div className="print-section-title">Billing Address</div>
                            <TableContainer component={Paper} sx={{ mb: 3 }}>
                                <Table size="small">
                                    <colgroup><col style={{ width: '40%' }} /><col style={{ width: '60%' }} /></colgroup>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Number / Street Name</TableCell>
                                            <TableCell>{formData?.generalInfo?.postalStreetNumber || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Suburb</TableCell>
                                            <TableCell>{formData?.generalInfo?.postalSuburb || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Post Code</TableCell>
                                            <TableCell>{formData?.generalInfo?.postalPostCode || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Country</TableCell>
                                            <TableCell>{formData?.generalInfo?.postalCountry || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>State</TableCell>
                                            <TableCell>{formData?.generalInfo?.postalState || '-'}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>

                        {/* Invoice / Statement Information */}
                        <div className="print-section-block">
                            <div className="print-section-title">Invoice / Statement</div>
                            <TableContainer component={Paper} sx={{ mb: 3 }}>
                                <Table size="small">
                                    <colgroup><col style={{ width: '40%' }} /><col style={{ width: '60%' }} /></colgroup>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Contact Name for Invoices</TableCell>
                                            <TableCell>{formData?.invoiceStatementInfo?.contactName || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Phone Number</TableCell>
                                            <TableCell>{formData?.invoiceStatementInfo?.phoneNumber || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Email Address for Invoices</TableCell>
                                            <TableCell>{formData?.invoiceStatementInfo?.email || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Email Address for Statements</TableCell>
                                            <TableCell>{formData?.invoiceStatementInfo?.emailForStatements || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Is PO number mandatory for invoicing?</TableCell>
                                            <TableCell>{toYesNo(formData?.invoiceStatementInfo?.isPONumberMandatory ?? formData?.invoiceStatementInfo?.isPoNumberMandatory)}</TableCell>
                                        </TableRow>                                        
                                        <TableRow>
                                            <TableCell>Email address for PO Number</TableCell>
                                            <TableCell>{formData?.generalInfo?.poEmail || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Does your company/corporation use an invoice platform for submitting & delivery of invoices?</TableCell>
                                            <TableCell>{toYesNo(formData?.invoiceStatementInfo?.usesInvoicePlatform)}</TableCell>
                                        </TableRow>                                        
                                        <TableRow>
                                            <TableCell>Name of Invoice Platform</TableCell>
                                            <TableCell>{formData?.invoiceStatementInfo?.invoicePlatform || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Do you wish to apply for a 30-day credit account?</TableCell>
                                            <TableCell>{toYesNo(formData?.invoiceStatementInfo?.needCreditAccount)}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>

                        {/* Credit Account Information (if applicable) */}
                        {formData?.invoiceStatementInfo?.needCreditAccount === 'yes' && (
                            <>
                                <div className="print-section-title">Credit Account Details</div>
                                <TableContainer component={Paper} sx={{ mb: 3 }}>
                                    <Table size="small">
                                        <colgroup><col style={{ width: '40%' }} /><col style={{ width: '60%' }} /></colgroup>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>Contact Name</TableCell>
                                                <TableCell>{formData?.creditAccountInfo?.contactName || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Email Address</TableCell>
                                                <TableCell>{formData?.creditAccountInfo?.email || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Position</TableCell>
                                                <TableCell>{formData?.creditAccountInfo?.position || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Phone Number</TableCell>
                                                <TableCell>{formData?.creditAccountInfo?.phoneNumber || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Company Website</TableCell>
                                                <TableCell>{formData?.creditAccountInfo?.companyWebsite || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Nature of Business</TableCell>
                                                <TableCell>{formData?.creditAccountInfo?.natureOfBusiness || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Date of Incorporation</TableCell>
                                                <TableCell>{formData?.creditAccountInfo?.dateOfIncorporation || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Parent Company (if any)</TableCell>
                                                <TableCell>{formData?.creditAccountInfo?.parentCompany || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Previous Business Name/s (if any)</TableCell>
                                                <TableCell>{formData?.creditAccountInfo?.previousBusinessNames || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Maximum Credit Required</TableCell>
                                                <TableCell>{formData?.creditAccountInfo?.maximumCreditRequired || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Is the applicant a trustee for any trust?</TableCell>
                                                <TableCell>{formData?.creditAccountInfo?.isTrustee || '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Name of Director/s or Proprietors</TableCell>
                                                <TableCell>{formData?.creditAccountInfo?.directorName || '-'}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Credit Referees */}
                                {formData?.creditAccountInfo?.hasCreditReferees === 'yes' && formData?.creditReferees?.length > 0 && (
                                    <>
                                        <div className="print-section-title">Credit Referees Details</div>
                                        <TableContainer component={Paper} sx={{ mb: 3 }}>
                                            <Table size="small">
                                                <colgroup><col style={{ width: '40%' }} /><col style={{ width: '60%' }} /></colgroup>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell>Would you wish to provide credit referees?</TableCell>
                                                        <TableCell>{formData?.creditAccountInfo?.hasCreditReferees || '-'}</TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <TableContainer component={Paper} sx={{ mb: 3 }}>
                                            <Table size="small">
                                                <colgroup><col style={{ width: '25%' }} /><col style={{ width: '25%' }} /><col style={{ width: '25%' }} /><col style={{ width: '25%' }} /></colgroup>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Contact Name</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Phone Number</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>Email Address</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {formData?.creditReferees?.map((referee: any, index: number) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{referee?.contactName || '-'}</TableCell>
                                                            <TableCell>{referee?.company || '-'}</TableCell>
                                                            <TableCell>{referee?.phone || '-'}</TableCell>
                                                            <TableCell>{referee?.email || '-'}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}
                            </>
                        )}

                        <div className="print-section-block">
                            <div className="print-section-title">Declaration</div>
                            <Box sx={{ mb: 4, pt: 1, pb: 3, px: 1, fontSize: '13px', lineHeight: 1.7 }}>
                                I hereby authorise Eurofins to investigate the listed ABN pertinent to my credit worthiness. I agree to abide by the of Eurofins. I agree to pay Eurofins invoices within payment terms and relevant legal fees in the event of unreasonable non-compliance to payment terms.
                            </Box>
                            <div className="print-section-title">Signature & Authorization</div>                            
                            <TableContainer component={Paper} sx={{ mb: 3 }}>
                                <Table size="small">
                                    <colgroup><col style={{ width: '40%' }} /><col style={{ width: '60%' }} /></colgroup>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Full Name</TableCell>
                                            <TableCell>{formData?.generalInfo?.fullName || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>{formData?.generalInfo?.requestedDate ? formatDate(formData.generalInfo.requestedDate) : '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Company</TableCell>
                                            <TableCell>{formData?.generalInfo?.company || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Position Title</TableCell>
                                            <TableCell>{formData?.generalInfo?.title || '-'}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <div className="print-section-title">Signature</div>
                            <Box sx={{ pt: 1, pb: 3, px: 1 }}>
                                {signatureImage ? (
                                    <img src={signatureImage} alt="Signature" style={{ maxWidth: '250px', maxHeight: '100px', height: 'auto', display: 'block' }} />
                                ) : (
                                    <span style={{ fontSize: '13px' }}>-</span>
                                )}
                            </Box>
                        </div>

                        {/* ELVIS Client Details */}
                        <div className="print-section-block">
                            <div className="print-section-title">ELVIS Client Details</div>
                            <TableContainer component={Paper} sx={{ mb: 3 }}>
                                <Table size="small">
                                    <colgroup><col style={{ width: '40%' }} /><col style={{ width: '60%' }} /></colgroup>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Price List</TableCell>
                                            <TableCell>{getPriceListName(formData?.elvisClientDetails?.priceList)}</TableCell>
                                        </TableRow>                                        
                                        <TableRow>
                                            <TableCell>Report State</TableCell>
                                            <TableCell>{getStateName(formData?.elvisClientDetails?.state)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>ASM</TableCell>
                                            <TableCell>{getAsmName(formData?.elvisClientDetails?.asm)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>ELE</TableCell>
                                            <TableCell>{formData?.elvisClientDetails?.ele || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>BU</TableCell>
                                            <TableCell>{formData?.elvisClientDetails?.bu || '-'}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>

                        {/* AR Review  */}
                        <div className="print-section-block">
                            <div className="print-section-title">AR Review</div>
                            <TableContainer component={Paper} sx={{ mb: 3 }}>
                                <Table size="small">
                                    <colgroup><col style={{ width: '40%' }} /><col style={{ width: '60%' }} /></colgroup>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Client Code</TableCell>
                                            <TableCell>{formData?.elvisClientDetails?.code || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Payment Terms</TableCell>
                                            <TableCell>{getPaymentTermsDescription(formData?.elvisClientDetails?.paymentTerms)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Country</TableCell>
                                            <TableCell>{formData?.elvisClientDetails?.country || 'Australia'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Bank Letters</TableCell>
                                            <TableCell>{formData?.elvisClientDetails?.bankLetterFileName || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Is PO Required</TableCell>
                                            <TableCell>{toYesNo(formData?.invoiceStatementInfo?.isPoNumberMandatory ?? formData?.invoiceStatementInfo?.isPONumberMandatory)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Is GST Exempt</TableCell>
                                            <TableCell>{formData?.elvisClientDetails?.country !== 'Australia' ? 'Yes' : 'No'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Is Eurofins Group</TableCell>
                                            <TableCell>{formData?.elvisClientDetails?.isEurofinsGroup ? 'Yes' : 'No'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>ABN Checked</TableCell>
                                            <TableCell>{formData?.elvisClientDetails?.abnCheck ? 'Yes' : 'No'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Referee Checked</TableCell>
                                            <TableCell>{formData?.elvisClientDetails?.refereeCheck ? 'Yes' : 'No'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Invoice Monthly</TableCell>
                                            <TableCell>{formData?.elvisClientDetails?.isInvoiceMonthly ? 'Yes' : 'No'}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>

                    </Box>
                </td></tr></tbody>
            </table>
        </div>
    );
};

export default PrintComponent;